import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { alpha, Box, Portal } from '@mui/material';
import { Button, IconButton, LoadingLogo, Stack, Text, useColors } from '@si/components';
import type { Translation } from '@si/utils';
import { useTranslate } from '@si/utils';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getGuidedTourLayout } from './guidedTourLayout';
import { learningContent as content } from './learningContent';

type TourStep = {
  additionalTargetId?: string;
  description: Translation;
  fallbackTargetId?: string;
  requiresHistoricalData?: boolean;
  targetId: string;
  title: Translation;
};

const TOUR_STEPS: TourStep[] = [
  {
    description: content.combinedOverviewDescription,
    targetId: 'data-tab',
    title: content.dataOverviewTitle,
  },
  {
    description: content.installationDescription,
    targetId: 'data-installation-navigation',
    fallbackTargetId: 'data-installation-selection',
    title: content.installationTitle,
  },
  {
    description: content.realtimeOverviewDescription,
    targetId: 'data-time-period-controls',
    fallbackTargetId: 'data-period-control',
    additionalTargetId: 'data-period-summary-control',
    title: content.realtimeOverviewTitle,
  },
  {
    description: content.sensorMetricDescription,
    targetId: 'data-analysis-options',
    title: content.sensorMetricTitle,
  },
  {
    description: content.mapOverviewDescription,
    targetId: 'data-data-map-canvas',
    fallbackTargetId: 'data-analysis-controls',
    title: content.mappingTitle,
  },
  {
    description: content.combinedGroupsDescription,
    targetId: 'data-combined-sensor-groups',
    requiresHistoricalData: true,
    title: content.sensorGroupTitle,
  },
  {
    description: content.chartInspectionDescription,
    targetId: 'data-analysis-charts',
    requiresHistoricalData: true,
    title: content.chartInspectionTitle,
  },
];

type TargetRect = Pick<DOMRect, 'bottom' | 'height' | 'left' | 'right' | 'top' | 'width'> & {
  borderRadius: string;
};
type HighlightHole = { height: number; left: number; radius: number; top: number; width: number };

function getTargetRect(element: Element): TargetRect {
  const { bottom, height, left, right, top, width } = element.getBoundingClientRect();
  return {
    borderRadius: window.getComputedStyle(element).borderRadius,
    bottom,
    height,
    left,
    right,
    top,
    width,
  };
}

function getHighlightBorderRadius(borderRadius: string) {
  const radius = Number.parseFloat(borderRadius);
  return `${Number.isFinite(radius) ? radius + 6 : 12}px`;
}

function targetRectsAreEqual(left: TargetRect | null, right: TargetRect | null) {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.bottom === right.bottom &&
    left.borderRadius === right.borderRadius &&
    left.height === right.height &&
    left.left === right.left &&
    left.right === right.right &&
    left.top === right.top &&
    left.width === right.width
  );
}

interface Props {
  onClose: () => void;
  onComplete: () => void;
  onStepChange: (step: number) => void;
  projectId: string;
  step: number;
}

export function GuidedTour({ onClose, onComplete, onStepChange, projectId, step }: Props) {
  const { border, primary, surface, textPrimary } = useColors();
  const { translate } = useTranslate();
  const location = useLocation();
  const navigate = useNavigate();
  const steps = TOUR_STEPS;
  const activeStep = steps[step] ?? steps[0];
  const [readyStep, setReadyStep] = useState<number | null>(null);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [contentBounds, setContentBounds] = useState(() => ({
    left: 16,
    right: window.innerWidth - 16,
  }));
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(250);
  const [hasWorkspace, setHasWorkspace] = useState(false);
  const [awaitingInstallationSelection, setAwaitingInstallationSelection] = useState(false);
  const [installationCount, setInstallationCount] = useState<number | null>(null);
  const visibleStepIndices = steps
    .map((_, index) => index)
    .filter((index) => installationCount !== 1 || index !== 1);
  const visibleStep = visibleStepIndices.indexOf(step);

  useEffect(() => {
    if (step !== 1) {
      setAwaitingInstallationSelection(false);
      return;
    }
    if (installationCount === 1 || (awaitingInstallationSelection && hasWorkspace)) {
      onStepChange(2);
    }
  }, [awaitingInstallationSelection, hasWorkspace, installationCount, onStepChange, step]);
  const dataSearchRef = useRef(location.pathname.includes('/data') ? location.search : '');
  const [viewport, setViewport] = useState(() => ({
    height: window.innerHeight,
    width: window.innerWidth,
  }));
  const targetPath = `/projects/${projectId}/data`;
  const isInstallationStep = activeStep.targetId === 'data-installation-navigation';
  const searchParams = new URLSearchParams(location.search);
  const isHistoricalView =
    searchParams.get('dataMode') === 'history' || searchParams.get('mapInstallationId') === 'all';
  const needsHistoricalData = Boolean(activeStep.requiresHistoricalData && !isHistoricalView);

  useEffect(() => {
    if (location.pathname.includes('/data')) dataSearchRef.current = location.search;
    if (location.pathname !== targetPath || needsHistoricalData) {
      const params = new URLSearchParams(dataSearchRef.current);
      if (needsHistoricalData) params.set('dataMode', 'history');
      const search = params.toString();
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(`${targetPath}${search ? `?${search}` : ''}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate, needsHistoricalData, targetPath]);

  useEffect(() => {
    if (!activeStep.targetId || location.pathname !== targetPath || needsHistoricalData) {
      setTargetRect(null);
      setHasWorkspace(false);
      return;
    }

    let observedElement: HTMLElement | null = null;
    let observedAdditionalElement: HTMLElement | null = null;
    const resizeObserver = new ResizeObserver(updateTarget);
    const mutationObserver = new MutationObserver(updateTarget);

    function updateTarget() {
      const main = document.getElementById('main');
      const mainRect = main?.getBoundingClientRect();
      const mainStyle = main ? window.getComputedStyle(main) : null;
      const bounds = {
        left: Math.max(
          16,
          (mainRect?.left ?? 0) + (Number.parseFloat(mainStyle?.paddingLeft ?? '') || 0)
        ),
        right: Math.min(
          window.innerWidth - 16,
          (mainRect?.right ?? window.innerWidth) -
            (Number.parseFloat(mainStyle?.paddingRight ?? '') || 0)
        ),
      };
      setContentBounds((current) =>
        current.left === bounds.left && current.right === bounds.right ? current : bounds
      );
      const navigation = document.getElementById('data-installation-navigation');
      setHasWorkspace(Boolean(navigation));
      const selection = document.getElementById('data-installation-selection');
      if (isInstallationStep && selection && !navigation) setAwaitingInstallationSelection(true);
      const scope = navigation ?? selection;
      const count = Number(scope?.getAttribute('data-installation-count'));
      if (count > 0) setInstallationCount(count);
      const element =
        document.getElementById(activeStep.targetId) ??
        (activeStep.fallbackTargetId ? document.getElementById(activeStep.fallbackTargetId) : null);
      if (!element) {
        setTargetRect((current) => (current ? null : current));
        return;
      }

      const additionalElement = activeStep.additionalTargetId
        ? document.getElementById(activeStep.additionalTargetId)
        : null;
      if (element !== observedElement || additionalElement !== observedAdditionalElement) {
        observedElement = element;
        observedAdditionalElement = additionalElement;
        element.scrollIntoView({ behavior: 'auto', block: 'center' });
        resizeObserver.disconnect();
        resizeObserver.observe(element);
        if (additionalElement) resizeObserver.observe(additionalElement);
      }

      let rect = getTargetRect(element);
      if (additionalElement) {
        const additionalRect = getTargetRect(additionalElement);
        const top = Math.min(rect.top, additionalRect.top);
        const bottom = Math.max(rect.bottom, additionalRect.bottom);
        rect = {
          ...rect,
          left: Math.min(rect.left, additionalRect.left),
          right: Math.max(rect.right, additionalRect.right),
          top,
          bottom,
          height: bottom - top,
        };
      }
      const left = Math.max(bounds.left, rect.left);
      const right = Math.min(bounds.right, rect.right);
      const nextRect = { ...rect, left, right, width: Math.max(0, right - left) };
      setTargetRect((current) => (targetRectsAreEqual(current, nextRect) ? current : nextRect));
    }
    const updateViewport = () => {
      setViewport({ height: window.innerHeight, width: window.innerWidth });
      updateTarget();
    };
    mutationObserver.observe(document.body, {
      attributeFilter: ['disabled', 'data-installation-count'],
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', updateViewport);
    window.addEventListener('scroll', updateTarget, true);
    updateTarget();

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('scroll', updateTarget, true);
      resizeObserver.disconnect();
    };
  }, [
    activeStep.additionalTargetId,
    activeStep.fallbackTargetId,
    activeStep.targetId,
    isInstallationStep,
    location.pathname,
    needsHistoricalData,
    targetPath,
  ]);

  const isTargetReady =
    location.pathname === targetPath && !needsHistoricalData && Boolean(targetRect);

  useEffect(() => {
    if (!isTargetReady) {
      setReadyStep(null);
      return;
    }

    const timeoutId = window.setTimeout(() => setReadyStep(step), 180);
    return () => window.clearTimeout(timeoutId);
  }, [isTargetReady, step]);

  const isTransitioning = readyStep !== step || !isTargetReady;

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const updateHeight = () => setCardHeight(card.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(card);
    return () => observer.disconnect();
  }, [isTransitioning]);

  useLayoutEffect(() => {
    if (isTransitioning || activeStep.targetId !== 'data-analysis-charts') return;
    const chart = document.getElementById(activeStep.targetId);
    if (!chart) return;
    // Leave room above the chart for the measured dialog and its pointer.
    const height = Math.min(cardHeight, viewport.height - 32 - 56 - 12);
    const desiredTop = height + 16 + 12 + 6;
    const offset = chart.getBoundingClientRect().top - desiredTop;
    if (Math.abs(offset) > 1) window.scrollBy({ top: offset, behavior: 'auto' });
  }, [activeStep.targetId, cardHeight, isTransitioning, viewport.height]);

  const layout = useMemo(
    () =>
      targetRect
        ? getGuidedTourLayout({
            bounds: contentBounds,
            cardHeight,
            preferredPlacement:
              activeStep.targetId === 'data-analysis-charts' ? 'above' : undefined,
            cardWidth: viewport.width < 700 ? contentBounds.right - contentBounds.left : 448,
            target: targetRect,
            viewportHeight: viewport.height,
          })
        : null,
    [activeStep.targetId, cardHeight, contentBounds, targetRect, viewport.height, viewport.width]
  );
  const highlightHoles = useMemo<HighlightHole[]>(
    () =>
      layout && targetRect
        ? [
            {
              ...layout.highlight,
              radius: Number.parseFloat(getHighlightBorderRadius(targetRect.borderRadius)),
            },
          ]
        : [],
    [layout, targetRect]
  );

  const dimmedOverlayMask = useMemo(() => {
    if (!highlightHoles.length) return undefined;

    const holes = highlightHoles
      .map(
        ({ height, left, radius, top, width }) =>
          `<rect x="${left}" y="${top}" width="${width}" height="${height}" rx="${radius}" fill="black"/>`
      )
      .join('');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${viewport.width}" height="${viewport.height}"><mask id="tour-cutouts" maskUnits="userSpaceOnUse"><rect width="${viewport.width}" height="${viewport.height}" fill="white"/>${holes}</mask><rect width="${viewport.width}" height="${viewport.height}" fill="white" mask="url(#tour-cutouts)"/></svg>`;

    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }, [highlightHoles, viewport.height, viewport.width]);

  const isLastStep = step === steps.length - 1;
  const titleId = 'data-tour-title';
  const descriptionId = 'data-tour-description';
  const advanceTour = () => {
    if (isLastStep) onComplete();
    else onStepChange(visibleStepIndices[visibleStep + 1]);
  };

  useEffect(() => {
    if (readyStep === step) document.getElementById(titleId)?.focus({ preventScroll: true });
  }, [readyStep, step]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <Portal>
      <Box
        aria-describedby={isTransitioning ? undefined : descriptionId}
        aria-labelledby={isTransitioning ? undefined : titleId}
        role="dialog"
        tabIndex={-1}
        sx={{
          inset: 0,
          pointerEvents: 'none',
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        {isTransitioning ? (
          <Box
            aria-hidden
            sx={{ backgroundColor: 'rgba(8, 10, 20, 0.58)', inset: 0, position: 'fixed' }}
          />
        ) : targetRect ? (
          <>
            <Box
              aria-hidden
              sx={{
                WebkitMaskImage: dimmedOverlayMask,
                WebkitMaskPosition: '0 0',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskSize: '100% 100%',
                backgroundColor: 'rgba(8, 10, 20, 0.58)',
                inset: 0,
                maskImage: dimmedOverlayMask,
                maskPosition: '0 0',
                maskRepeat: 'no-repeat',
                maskSize: '100% 100%',
                pointerEvents: 'none',
                position: 'fixed',
              }}
            />
            <Box
              aria-hidden
              data-tour-highlight
              sx={{
                border: `2px solid ${primary}`,
                boxSizing: 'border-box',
                borderRadius: getHighlightBorderRadius(targetRect.borderRadius),
                height: highlightHoles[0]?.height ?? 0,
                left: highlightHoles[0]?.left,
                pointerEvents: 'none',
                position: 'fixed',
                top: highlightHoles[0]?.top,
                width: highlightHoles[0]?.width ?? 0,
              }}
            />
          </>
        ) : (
          <Box
            aria-hidden
            sx={{
              backgroundColor: 'rgba(8, 10, 20, 0.58)',
              inset: 0,
              pointerEvents: 'none',
              position: 'fixed',
            }}
          />
        )}

        {isTransitioning ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ inset: 0, pointerEvents: 'none', position: 'fixed' }}
          >
            <Box
              sx={{
                backgroundColor: surface,
                border: '1px solid',
                borderColor: border,
                borderRadius: '0.75rem',
                boxShadow: 12,
                padding: '1.25rem',
              }}
            >
              <LoadingLogo size="2rem" variant="energyScanner" />
            </Box>
          </Stack>
        ) : (
          <Box
            ref={cardRef}
            data-tour-card
            data-placement={layout?.placement}
            sx={{
              left: layout?.card.left,
              top: layout?.card.top,
              width: layout?.card.width,
              maxHeight: layout?.card.maxHeight,
              backgroundColor: surface,
              border: '1px solid',
              borderColor: alpha(primary, 0.65),
              borderRadius: '0.75rem',
              boxShadow: 12,
              color: textPrimary,
              boxSizing: 'border-box',
              maxWidth: contentBounds.right - contentBounds.left,
              pointerEvents: 'auto',
              position: 'fixed',
            }}
          >
            {layout && (
              <Box
                aria-hidden
                data-tour-arrow
                sx={{
                  position: 'absolute',
                  left: layout.arrow.left,
                  top: layout.arrow.top,
                  width: 12,
                  height: 12,
                  backgroundColor: surface,
                  border: `1px solid ${alpha(primary, 0.65)}`,
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  pointerEvents: 'none',
                }}
              />
            )}
            <Box
              sx={{
                position: 'relative',
                backgroundColor: surface,
                borderRadius: 'inherit',
                maxHeight: Math.max(0, (layout?.card.maxHeight ?? viewport.height - 32) - 2),
                boxSizing: 'border-box',
                overflowY: 'auto',
                padding: '1.25rem',
              }}
            >
              <Stack gap="1rem">
                <Stack
                  alignItems="flex-start"
                  direction="row"
                  flexWrap="nowrap"
                  justifyContent="space-between"
                >
                  <Stack flex="1 1 auto" gap="0.25rem" minWidth={0}>
                    <Text color="darkGrey" fontSize="0.75rem">
                      {translate(content.tourStep, {
                        current: visibleStep + 1,
                        total: visibleStepIndices.length,
                      })}
                    </Text>
                    <Box
                      component="h2"
                      id={titleId}
                      tabIndex={-1}
                      sx={{ fontSize: '1.25rem', margin: 0 }}
                    >
                      {translate(activeStep.title)}
                    </Box>
                  </Stack>
                  <Box sx={{ flex: '0 0 auto' }}>
                    <IconButton
                      ariaLabel={translate(content.closeGuide)}
                      icon={CloseOutlined}
                      iconSize="small"
                      onClick={onClose}
                      size="small"
                    />
                  </Box>
                </Stack>

                <Box id={descriptionId} sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                  {translate(activeStep.description)}
                </Box>

                <Stack alignItems="center" direction="row" justifyContent="space-between">
                  <Box />
                  <Stack direction="row" gap="0.5rem">
                    {step > 0 && (
                      <Button
                        onClick={() => onStepChange(visibleStepIndices[visibleStep - 1])}
                        size="small"
                        variant="outlined"
                      >
                        {translate(content.previous)}
                      </Button>
                    )}
                    <Button
                      disabled={
                        (step === 0 && installationCount === null) ||
                        (isInstallationStep && !hasWorkspace)
                      }
                      onClick={advanceTour}
                      size="small"
                    >
                      {translate(isLastStep ? content.finish : content.next)}
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Portal>
  );
}
